/**
 * The menu — client revision (Sep 2026): one flat menu, no featured block.
 *
 * The Works sits as the LAST row of the haircuts list (it is last in the
 * config services array) with a muted subline naming what's in it — all
 * services and prices live in this single list now. Every name and price
 * comes from shop.config.json; price_display is used verbatim because some
 * prices carry a "+" (Designs $30+) that a bare number would lose. The old
 * headline/subhead/featured keys stay in the dictionaries, unrendered.
 */

import { useCopy } from '../../content';
import { useShopConfig } from '../../hooks/useShopConfig';

const WORKS_SLUG = 'the-works';

export default function ServicesTeaser() {
  const c = useCopy().home.menu;
  const { t, serviceName, shop, services, addOns } = useShopConfig();


  return (
    <section id="menu" className="relative bg-paper py-24 md:py-32">
      <div className="mx-auto max-w-[1240px] px-6 md:px-12">
        {/* The menu board's own title. There is no eyebrow above it any
            more — the section id is what the nav lands on, not the tag. */}
        <div>
          <h2 className="font-oswald text-[26px] font-medium uppercase tracking-[0.2em] text-navy md:text-[32px]">
            {t(c.haircutsLabel)}
          </h2>
          <span aria-hidden className="mt-4 block h-px w-full max-w-[420px] bg-gold" />

          <ul className="mt-8 space-y-5">
            {services.map((item) => (
              <li key={item.slug}>
                <div className="flex items-baseline gap-4">
                  <span className="font-newsreader text-[20px] font-medium text-navy md:text-[22px]">
                    {serviceName(item)}
                  </span>
                  <span className="flex-1 border-b border-dotted border-navy/20" />
                  <span className="font-newsreader text-[20px] italic text-navy/80">
                    {item.price_display}
                  </span>
                </div>
                {item.slug === WORKS_SLUG && (
                  <p className="mt-1 font-newsreader text-[15px] italic text-navy/55">
                    {t(c.worksSubline)}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-12 rounded-2xl bg-navy/5 p-8 md:p-10">
          <p className="font-oswald text-[11px] font-medium uppercase tracking-[0.22em] text-navy/60">
            {t(c.addonsLabel)}
          </p>
          <p className="mt-2 font-newsreader text-[15px] italic text-navy/60">
            {t(c.addonsSubhead)}
          </p>
          <ul className="mt-6 space-y-3">
            {addOns.map((item) => (
              <li key={item.slug} className="flex items-baseline gap-3 text-[16px]">
                <span className="font-newsreader text-navy/85">{serviceName(item)}</span>
                <span className="flex-1 border-b border-dotted border-navy/15" />
                <span className="font-newsreader italic text-navy/70">{item.price_display}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-12 text-center">
          <p className="font-newsreader text-[18px] italic text-navy/70">{t(c.softCtaIntro)}</p>
          {/* Phase 1: landline (813-355-3442) doesn't receive SMS. Call-only
              until GHL provisions the text-enabled number post-A2P approval.
              Phase 2: swap tel: back to sms: and restore "Text Kenny" copy. */}
          <a
            href={`tel:${shop.phone_e164}`}
            className="mt-3 inline-flex items-center gap-2 border-b border-gold pb-1 font-oswald text-[13px] font-medium uppercase tracking-[0.2em] text-navy transition-colors hover:text-gold"
          >
            {t(c.softCtaCall)}
            <span aria-hidden>→</span>
          </a>
        </div>
      </div>
    </section>
  );
}
